/**
 * WebSmCase — the case they unlocked on the Difficulty step, open in front of
 * them, a laptop in it with the Enemy's seal flickering on its screen. Then in
 * through the screen to the briefing (counterstrike.tsx): the Enemy's strike,
 * their counterstrike (the mission), and other agents' tactics. Its accept
 * button accepts the mission: it emits the case's click (CLICK1-3, by tier), as the
 * Difficulty tap used to. Its own step, so a refresh, or a back from the live
 * mission, comes back to the open case, never to the locked one. The
 * mission is always handed over here, on the web — never by texting a code
 * to the handler.
 *
 * Options (the Difficulty step's fill in what this one does not set):
 *   CaseTitle / CasePrompt / DeskImage / Diff{1,2,3}Label / AcceptButton
 *   / OnClockMessage / OnClockButton / BgColor / AccentColor.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { useFunnelStore, useStepperStore } from "@/store";
import { useDailyDoors, missionIdFor } from "@/features/funnel/api/use-doors";
import { useMission } from "@/features/missionkit/api/use-mission";
import { useHasPhone } from "@/features/funnel/hooks/use-phone-login";
import type { Tier } from "@/features/funnel/types";
import { ACTION_BTN_CLASS, stepVars } from "./step-shell";
import type { StepTypeProps } from "./types";
import {
  FRONT_WALL_CLIP,
  OPEN_CASES,
  OPEN_CASE_STYLE,
  screenCentre,
} from "./open-case";
import { deskFor } from "./desk-art";
import { Laptop } from "./laptop";
import { Counterstrike } from "./counterstrike";
import { OnTheClock } from "./on-the-clock";
import { comebackLoopEnabled } from "./comeback-config";
import { useMissionFile } from "@/features/funnel/api/use-mission-file";

/** The cases, in the Difficulty step's order (CLICK1-3). */
const TIERS: Tier[] = ["beginner", "challenging", "improbable"];
/** How long the seal flickers on the laptop before they go in, and the
 *  zoom in through its screen. */
const FLICKER_MS = 2600;
const ZOOM_MS = 900;

export default function WebSmCaseType({ getOpt, dispatch }: StepTypeProps) {
  const bg = getOpt("BgColor") || "#0a0809";
  const accent = getOpt("AccentColor") || "#d6a94e";
  const handler = useFunnelStore((s) => s.handler);
  const desk = deskFor(handler, getOpt("DeskImage"));
  const title = getOpt("CaseTitle") || "Your mission case";
  const prompt = getOpt("CasePrompt") || "Incoming from the Enemy.";
  const acceptButton = getOpt("AcceptButton") || "Accept mission";

  // the case unlocked on the desk (or, coming back, the one accepted)
  const caseTier = useFunnelStore((s) => s.caseTier);
  const acceptedTier = useFunnelStore((s) => s.tier);
  const tier = caseTier ?? acceptedTier ?? "beginner";
  const i = Math.max(TIERS.indexOf(tier), 0);
  const event = `CLICK${i + 1}`;

  // The laptop in the case first, the Enemy's seal flickering on it; then
  // in through its screen to the briefing. Back from the live mission it
  // has been seen already.
  const cameBack = useStepperStore((s) => s.cameBack);
  const [stage, setStage] = useState<"laptop" | "zoom" | "brief">(
    cameBack ? "brief" : "laptop",
  );
  const zoomIn = () => {
    if (stage === "laptop") setStage("zoom");
  };
  useEffect(() => {
    if (stage !== "zoom") return;
    const t = window.setTimeout(() => setStage("brief"), ZOOM_MS);
    return () => window.clearTimeout(t);
  }, [stage]);
  // Accepted on the web: the agent sends them off on the clock before the
  // live mission (the accept itself goes out on "On it").
  const [accepted, setAccepted] = useState(false);
  const { data: hasPhone } = useHasPhone();

  // The mission this door + case resolves to — the same id reported on
  // accept, so the file and the stepper can't disagree.
  const door = useFunnelStore((s) => s.door);
  const { data: doors } = useDailyDoors();
  // back from the live mission: the one accepted, as it was (the doors have
  // moved on once it was filed, or at midnight)
  const acceptedId = useFunnelStore((s) => s.missionId);
  const missionId =
    cameBack && acceptedId ? acceptedId : missionIdFor(doors, door, tier);
  const { data: mission, isLoading: missionLoading } = useMission(missionId);
  // a repeat: the briefing at half length (never for Mission Improbable)
  const { data: filed = [] } = useMissionFile();
  const quick =
    comebackLoopEnabled() && filed.length > 0 && tier !== "improbable";
  // Empty only once there is an answer: not while the doors (and so the id)
  // are still on their way — a disabled query is not "loading" either.
  const resolved = (cameBack && !!acceptedId) || !!doors;
  const empty = resolved && !mission && !missionLoading;
  // An empty file's way out: another case on the desk, or (no desk on the
  // first mission) another door.
  const goTo = useStepperStore((s) => s.goTo);
  const steps = useStepperStore((s) => s.graph?.steps);
  const chooseAgain = () =>
    goTo(
      steps?.["Difficulty"]
        ? "Difficulty"
        : steps?.["First doors"]
          ? "First doors"
          : "Doors",
    );

  // the seal flickers a while, then in they go (or sooner, on a tap)
  useEffect(() => {
    if (!mission || stage !== "laptop") return;
    const t = window.setTimeout(zoomIn, FLICKER_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission, stage]);

  const sectionVars = {
    ...stepVars({ accent, bg }),
    "--sm-bg": bg,
  } as CSSProperties;

  return (
    <section
      style={sectionVars}
      className="relative h-[100dvh] overflow-clip font-serif bg-[var(--sm-bg)]"
    >
      <img
        src={desk}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[50%_100%]"
      />
      {/* keep the title legible over the room */}
      <div className="absolute inset-x-0 top-0 h-[22%] bg-[linear-gradient(180deg,rgba(0,0,0,.72),transparent)]" />
      <div className="pointer-events-none absolute top-[26px] right-0 left-0 z-[6] px-5 text-center">
        <div className="mb-[9px] font-stencil text-[10.5px] tracking-[4px] text-[#d6a94e] uppercase [text-shadow:0_0_14px_rgba(214,169,78,.5)]">
          ▸ {title} ◂
        </div>
        <div className="mx-auto max-w-[460px] font-stencil text-[clamp(13px,3.6cqw,17px)] leading-[1.35] tracking-[1.5px] text-[#eadfc6] uppercase [text-shadow:0_0_18px_rgba(214,169,78,.3),0_2px_14px_rgba(0,0,0,.9)]">
          {prompt}
        </div>
        <div className="mx-auto mt-3 h-px w-11 bg-[linear-gradient(90deg,transparent,#d6a94e,transparent)]" />
      </div>

      {/* the case, open: the laptop in it, the Enemy's seal flickering on
          its screen; then in through the screen to the briefing */}
      {stage !== "brief" ? (
        <div className="absolute inset-0 z-[5]">
          <div className="absolute inset-0 animate-[smDim_.45s_ease_both] bg-black/45" />
          <div
            style={{
              ...OPEN_CASE_STYLE,
              transformOrigin: screenCentre(tier),
              animation:
                stage === "zoom"
                  ? `smIntoScreen ${ZOOM_MS}ms cubic-bezier(.6,0,.9,.5) both`
                  : undefined,
            }}
            className="absolute animate-[smCaseOpen_.6s_cubic-bezier(.2,.8,.3,1)_both] [container-type:inline-size]"
          >
            <img
              src={OPEN_CASES[tier]}
              alt=""
              className="pointer-events-none absolute inset-0 size-full max-w-none"
            />
            <Laptop
              tier={tier}
              label="Open the briefing"
              onClick={mission ? zoomIn : undefined}
            />
            <img
              src={OPEN_CASES[tier]}
              alt=""
              style={{ clipPath: FRONT_WALL_CLIP }}
              className="pointer-events-none absolute inset-0 z-[2] size-full max-w-none"
            />
          </div>
          {/* the screen's black, filling the view as they go in */}
          {stage === "zoom" ? (
            <div
              style={{
                animation: `smDim ${ZOOM_MS * 0.4}ms ease-in ${ZOOM_MS * 0.35}ms both`,
              }}
              className="absolute inset-0 bg-black"
            />
          ) : null}
          {empty ? <EmptyFile onChoose={chooseAgain} /> : null}
        </div>
      ) : mission ? (
        <div className="absolute inset-0 z-[7] bg-black">
          <Counterstrike
            tier={tier}
            mission={mission}
            quick={quick}
            acceptLabel={acceptButton}
            onAccept={() => {
              // back from the live mission: on to it again, its clock as it
              // was (the player does not accept it a second time)
              if (cameBack && acceptedId) {
                dispatch(event);
                return;
              }
              // the clock starts now; the accept (on OK) keeps this time
              useFunnelStore.setState({ clockFrom: Date.now() });
              // No number yet (their first mission asks for none): nothing
              // gets texted, so no "I'll text you the mission" — straight on.
              if (!hasPhone) dispatch(event);
              else setAccepted(true);
            }}
          />
        </div>
      ) : empty ? (
        <div className="absolute inset-0 z-[7] bg-black">
          <EmptyFile onChoose={chooseAgain} />
        </div>
      ) : null}

      {accepted ? (
        <OnTheClock
          handler={handler}
          tier={tier}
          message={
            getOpt("OnClockMessage") ||
            "You're on the clock. Twenty-four hours, starting now. I'll text you the mission, so you have it on you wherever you go."
          }
          button={getOpt("OnClockButton") || "OK"}
          onGo={() => dispatch(event)}
        />
      ) : null}

      <style>{`
        @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes smCaseOpen{from{opacity:0;scale:.92}to{opacity:1;scale:1}}
        @keyframes smDim{from{opacity:0}to{opacity:1}}
        @keyframes smIntoScreen{from{scale:1}to{scale:6}}
      `}</style>
    </section>
  );
}

/** A case with no mission behind it: say so, and offer the way out. */
function EmptyFile({ onChoose }: { onChoose: () => void }) {
  return (
    <div className="absolute inset-x-0 bottom-[12%] flex flex-col items-center gap-3 px-6 text-center">
      <div className="font-stencil text-[14px] tracking-[2px] text-[#eadfc6] uppercase">
        This file is empty.
      </div>
      <button
        type="button"
        onClick={onChoose}
        className={`${ACTION_BTN_CLASS} rounded-[9px] px-6 py-3 text-[15px] font-bold`}
      >
        Choose a different one
      </button>
    </div>
  );
}
